import {
  FC, Fragment, useEffect, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { GridRowsProp, GridValidRowModel } from '@mui/x-data-grid';
import moment from 'moment-timezone';
import SvgIcon from '@mui/material/SvgIcon';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import { MemoizedBaseAvatar, BaseDialog, TDialogAction } from '@mgm/common/components/base';
import { getTimezone } from '@mgm/common/utils';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/useAppStore';
import { getIsOpenBulkDialog } from '../../../../../store/schedules/selectors';
import { schedulesActions } from '../../../../../store/schedules/slice';
import { ReactComponent as ShiftIcon } from '../../../../../static/icons/ic_today.svg';
import { ReactComponent as ClockIcon } from '../../../../../static/icons/ic_clock.svg';
import { ReactComponent as TrashIcon } from '../../../../../static/icons/ic_trash.svg';
import { ReactComponent as BreakIcon } from '../../../../../static/icons/ic_break.svg';
import { processShiftTimes } from '../../../../../utils/helpers';
import { getAuthenticateUser } from '../../../../../store/authenticate/selectors';
import { useAddScheduleShiftAPI } from '../../../../../hooks/api/useSchedules';
import { ROUTES } from '../../../../../data';

type TBulkConfirmDialog = {
  rows: GridRowsProp
  isInformEmployees: boolean
  setRows: any
};

const BulkConfirmDialog: FC<TBulkConfirmDialog> = ({ rows, isInformEmployees, setRows }) => {
  const { t } = useTranslation(['timesheets', 'common', 'team', 'schedule']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    mutateAsync: addShiftMutate,
    isLoading: isAddShiftLoading,
    isSuccess,
  } = useAddScheduleShiftAPI(false);

  const { enqueueSnackbar } = useSnackbar();
  const { personnel } = useAppSelector(getAuthenticateUser);

  const isOpenBulkDialog = useAppSelector(getIsOpenBulkDialog);

  useEffect(() => {
    if (isSuccess) {
      const goTo = ROUTES.WORK_SCHEDULES;
      enqueueSnackbar(`${t('shiftsWereAdded')}`, { variant: 'success' });
      navigate(goTo);
    }
  }, [isSuccess]);

  const handleCloseDialog = () => {
    dispatch(schedulesActions.setIsOpenBulkDialog(false));
  };

  const getValidShifts = useMemo(() => rows.filter((row) => row.employee.email), [rows]);

  const isValidObject = (shift: GridValidRowModel) => shift.location?.name && moment(shift.date).isValid() && shift.employee?.email;

  const isValidShifts = () => {
    const rowsWithData = rows.filter((row) => row.employee?.email);

    return rowsWithData?.length ? rowsWithData.every(isValidObject) : false;
  };

  const saveShifts = async () => {
    if (!isValidShifts()) {
      enqueueSnackbar(`${t('oneOrMoreShiftsAreInvalid', { ns: 'team' })}`, {
        variant: 'warning',
      });
      return;
    }

    const shifts = getValidShifts.map((row) => {
      const results = processShiftTimes(row?.date, row?.startTime, row?.endTime);
      const breakData = processShiftTimes(row?.date, row?.breakStart, row?.breakEnd);

      return {
        position: row.employee.position,
        avatarFile: row.employee.avatarFile,
        name: row.employee.name,
        email: row.employee.email,
        department: row.employee.department,
        location: row.location,
        locale: row.employee.locale,
        start: results?.startUTC.format() as string,
        end: results?.endUTC.format() as string,
        notes: [],
        tasks: [],
        timezone: getTimezone(),
        shopName: personnel.shopName,
        ...(breakData && {
          breaks: [{
            breakStart: breakData?.startUTC.format() as string,
            breakEnd: breakData?.endUTC.format() as string,
            id: 1,
          }],
        }),
      };
    });

    const data = {
      schedulesData: shifts.filter((shift) => shift.email),
      notifyEmployees: isInformEmployees,
      locals: shifts.map((shift) => ({ email: shift.email, locale: shift.locale || 'en' })),
    };

    await addShiftMutate(data);
    handleCloseDialog();
  };

  const dialogActions: Array<TDialogAction> = [
    {
      label: t('cancel', { ns: 'common' }),
      variant: 'outlined',
      color: 'primary',
      isDisabled: isAddShiftLoading,
      onClick: () => handleCloseDialog(),
    },
    {
      label: t('addShifts'),
      color: 'primary',
      isDisabled: !getValidShifts.length,
      isLoading: isAddShiftLoading,
      onClick: () => saveShifts(),
    },
  ];

  const deleteShift = (id: number) => {
    setRows(rows.filter((row) => row?.id !== id));
  };

  return (
    <BaseDialog
      dialogWidth={400}
      dialogContent={(
        <>
          {getValidShifts?.length ? (
            <Typography variant="body1" pb={2}>
              {t('youAreAbout', { ns: 'team' })}
              {' '}
              {getValidShifts?.length}
              {' '}
              {t('scheduleRecords', { ns: 'schedule' })}
              {' '}
              {t('continue', { ns: 'team' })}
            </Typography>
          ) : (
            <CustomNoRowsOverlay text={t('pleaseAddAtLeastOneShift', { ns: 'schedule' })} />
          )}
          <List sx={{
            width: '100%', maxWidth: 500, bgcolor: 'background.paper',
          }}
          >
            {getValidShifts.map((item, index) => (
              <Fragment key={`${item.employee?.email}-${index?.toString()}`}>
                <ListItem
                  sx={{
                    px: 0,
                  }}
                  alignItems="flex-start"
                  secondaryAction={(
                    <Box onClick={() => deleteShift(item.id)} component="div">
                      <SvgIcon
                        sx={{
                          mr: 1,
                        }}
                        className="cursor-pointer"
                        style={{
                          width: '1.4rem',
                          height: '1.4rem',
                        }}
                        component={TrashIcon}
                        inheritViewBox
                      />
                    </Box>
                )}
                >
                  <ListItemAvatar>
                    <MemoizedBaseAvatar avatarFile={item.employee.avatarFile} avatarName={item.employee.name} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={(
                      <Box alignItems="center" display="flex" component="span">
                        <SvgIcon
                          sx={{
                            mr: 1,
                          }}
                          color="primary"
                          component={ShiftIcon}
                          inheritViewBox
                        />
                        <Typography variant="body2">{moment(item.date).format('MMMM Do YYYY')}</Typography>
                      </Box>
                  )}
                    secondary={(
                      <Stack>
                        <Box mt={1} alignItems="center" display="flex" component="span">
                          <SvgIcon
                            sx={{
                              mr: 1,
                            }}
                            component={ClockIcon}
                            inheritViewBox
                          />
                          <Typography pr={1} variant="body2">
                            {moment(item.startTime).format('hh:mm A')}
                          </Typography>
                          -
                          <Typography pl={1} variant="body2">
                            {moment(item.endTime).format('hh:mm A')}
                          </Typography>
                        </Box>
                        {item.breakStart && (
                        <Box mt={1} alignItems="center" display="flex" component="span">
                          <SvgIcon
                            sx={{
                              mr: 1,
                            }}
                            component={BreakIcon}
                            inheritViewBox
                          />
                          <Typography pr={1} variant="body2">
                            {moment(item.breakStart).format('hh:mm A')}
                          </Typography>
                          -
                          <Typography pl={1} variant="body2">
                            {moment(item.breakEnd).format('hh:mm A')}
                          </Typography>
                        </Box>
                        )}
                      </Stack>
                  )}
                  />
                </ListItem>
                <Divider
                  sx={{
                    borderStyle: 'dashed',
                  }}
                  variant="inset"
                  component="li"
                />
              </Fragment>
            ))}
          </List>
        </>
      )}
      isOpenDialog={isOpenBulkDialog}
      dialogTitle={t('confirmAction', { ns: 'common' })}
      handleClose={handleCloseDialog}
      dialogActions={dialogActions}
    />
  );
};

export {
  BulkConfirmDialog,
};
